import { FC, useCallback, useEffect, useMemo } from "react"
import { Formik, Form, FormikValues } from 'formik'
import * as Yup from 'yup'
import { useUserLoader } from "../../../../../core/users"

import { KTSVG } from "../../../../../../../_metronic/helpers"
import { sortBy, remove } from "lodash"

import { FormikDateSelector, FormikSelect } from "../../../../../../components/widgets"
import { SubmitButtonRow } from "../../../../../../components/layout"
import { StatusErrorMessage } from "../../../../../../components/widgets"
import { doFormSubmit, isPopulatedArray } from "../../../../../../components/helpers"

import { getLastPathFromWorkflow, reassignDLMApplication } from "../../../../../core/doclabel"
import { useQueryClient } from "react-query"

export const reassignInitValues = {
  role: '',  
  appState: '',
  id: 0,
  inspectionDate: '',  
}

const reassignSchema: any = Yup.object({
  role: Yup.string().trim(),
  appState: Yup.string().trim(),
  id: Yup.number().integer().label('candidate')
  .when(['role'], {
    is: (role: string) => (role !== 'DLM Inspector'),
    then: (schema) => schema.required().moreThan(0, 'Select replacement')}),
  inspectionDate: Yup.string().trim().label('Inspection Date')
});


type Props = {
  appID: number  
  appState: string
  workflow: string
  role: string
  assignedId: number
  doClose: Function        
  siteApplicationid?: number
}


const DLMApplicationReassignModal: FC<Props> = ({appID, appState, workflow, role, assignedId, doClose, siteApplicationid = 0}) => {

  const queryClient = useQueryClient();
  
  const select = useCallback((data: any) => {      
    // exclude current assignee only if role is not 'DLM Inspector'
    if (! isPopulatedArray(data)) return [];
    if (role !== 'DLM Inspector') {
      remove(data, (o: any) => (o.id && o.id > 0 && o.id === assignedId));
    }      
    
    if (! isPopulatedArray(data)) return [];
    
    return sortBy(
      data.map((o: any) => ({ value: o.id, text: o.name})), 
      ['text'], ['asc'] 
    );
  }, [role, assignedId]);

  const { data: candidates } = useUserLoader({userType: role, select });
  
  const initialValues = useMemo(() => {
    if (! (role && appState)) return {...reassignInitValues};

    return {
      ...reassignInitValues,
      role,
      appState,
    }    

  }, [role, appState]);        

  const doSubmit = async (values: any, actions: FormikValues) => {

    const { setStatus, setSubmitting } = actions;            

    let payload = {
      id : parseInt(values.id)
    }

    if (role === 'DLM Inspector') {
      if (! (values.id > 0 || values.inspectionDate.length > 0)) {
        setStatus('Select new DLM Inspector or Inspection Date');
        setSubmitting(false);
        return;
      }

      if (values.id === 0) {
        Object.assign(payload, { id: assignedId});        
      }
      if (values.inspectionDate) {
        Object.assign(payload, { inspectionDate: values.inspectionDate });        
      }
    }

    if (siteApplicationid) {
      Object.assign(payload, {siteApplicationid});
    } else { 
      Object.assign(payload, {applicationid: appID});      
    }
    
    await doFormSubmit(reassignDLMApplication, payload, setStatus, setSubmitting, () => {
      queryClient.invalidateQueries(['DLMAPPLICATION-LOAD', { dlmappid: appID } ]);
      queryClient.invalidateQueries(['DLMAPPLICATIONS-LIST', { lastpath: getLastPathFromWorkflow(workflow)} ]);
      queryClient.invalidateQueries(['DLMAPPLICATIONS-LIST', { listmode: 'own'} ]);
      doClose();
    });
  }

  useEffect(() => {
    document.body.classList.add('modal-open')
    return () => {
      document.body.classList.remove('modal-open')
    }
  }, [])

  return(
  <>
  <div
    className='modal fade show d-block'
    id='kt_modal_reassign_dlm_application'
    role='dialog'
    tabIndex={-1}
    aria-modal='true'
  >    
    <div className='modal-dialog modal-dialog-centered mw-300px'>    
      <div className='modal-content'>
        <div className='modal-header'>      
          <h4 className="font-bold">
            { siteApplicationid ? 'Reassign Labelling' : 'Reassign Application'}            
          </h4>
          <div
            className='btn btn-icon btn-sm btn-active-icon-danger'
            data-kt-reassign-dlm-application-modal-action='close'        
            style={{cursor: 'pointer'}}
            onClick={() => doClose()}
          >
            <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-1' />
          </div>      
        </div>
        <div className='modal-body'>
          <Formik
            initialValues={initialValues}
            validationSchema={reassignSchema}
            enableReinitialize={true}
            onSubmit={doSubmit}
          >
            {({status}) => (
            <Form id='kt_doclabel_application_reassign_modal_form' className='form' noValidate>
              <div
                className='d-flex flex-column my-4 px-4'              
              >
                <div className="row mb-4">                
                  <FormikSelect size="12" label={`Assign ${role}`} name="id" required={role !== 'DLM Inspector'} options={candidates} placeholder={`Select ${role}`} />
                </div>
                { (role === 'DLM Inspector') && 
                <div className="row mb-4">                
                  <FormikDateSelector size="12" label="Inspection Date" name="inspectionDate" showShiftSelector={true} dateFormat='LL' />
                </div>
                }
                <StatusErrorMessage status={status} />
                <SubmitButtonRow doClose={doClose} />                        
              </div>
            </Form>
            )}
          </Formik>
        </div>                
      </div>
    </div>            
  </div>    
  <div className='modal-backdrop fade show'></div>    
  </>
  )
}

export { DLMApplicationReassignModal }